import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  SimpleGrid,
  Checkbox,
  Box,
  HStack,
  IconButton,
  VStack,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import type {
  CausaJudicial,
  EstadoCausa,
  TipoCausa,
  TipoMulta,
  Testigo,
  Involucrado,
} from './types';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';
// arriba del componente:
// ---------------------------
// Catálogos tipados
// ---------------------------
type CausaArchivoRow = {
  id: number;
  tipo: 'ACTA_INFRACCION' | 'ORDEN_GRUA' | 'PDF' | 'FOTO' | string;
  url: string;
  mime: string;
  original_name: string;
  filename: string;
};

export const ENTIDADES_MX = [
  'Aguascalientes',
  'Baja California',
  'Baja California Sur',
  'Campeche',
  'Chiapas',
  'Chihuahua',
  'Ciudad de México',
  'Coahuila de Zaragoza',
  'Colima',
  'Durango',
  'Estado de México',
  'Guanajuato',
  'Guerrero',
  'Hidalgo',
  'Jalisco',
  'Michoacán',
  'Morelos',
  'Nayarit',
  'Nuevo León',
  'Oaxaca',
  'Puebla',
  'Querétaro',
  'Quintana Roo',
  'San Luis Potosí',
  'Sinaloa',
  'Sonora',
  'Tabasco',
  'Tamaulipas',
  'Tlaxcala',
  'Veracruz',
  'Yucatán',
  'Zacatecas',
] as const;
export type EntidadFederativa = (typeof ENTIDADES_MX)[number];

export const LICENCIAS_FEDERALES = [
  'Tipo A: Automóviles particulares',
  'Tipo B: Carga ligera, sin articulación ni materiales peligrosos',
  'Tipo C: Carga pesada de dos o más ejes, sin materiales peligrosos',
  'Tipo D: Transporte turístico o chofer-guía',
  'Tipo E: Tractocamiones articulados o con materiales peligrosos',
  'Tipo F: Transporte de pasajeros a aeropuertos y puertos',
] as const;

export const LICENCIAS_ESTAT_TLX = [
  'Tipo A: Transporte público (taxis, combis, autobuses locales)',
  'Tipo B: Carga ligera o uso mixto (particular + carga)',
  'Tipo C: Automóviles particulares',
  'Tipo D: Motocicletas',
  'Tipo F: Carga pesada o remolques',
  'Permiso para menores: Conducción con restricciones',
] as const;

export const TIPOS_VEHICULO = [
  'Sedán',
  'Hatchback',
  'Motocicleta',
  'SUV',
  'Coupé',
  'Convertible',
  'Pickup / Camioneta',
  'Minivan / Van',
  'Camión',
  'Camioneta de carga',
  'Microbús',
  'Autobús',
  'Ciclomotor',
  'Motocross / Enduro',
  'Tractocamión',
  'Tráiler',
] as const;

// ---------------------------
// Catálogos propios del módulo
// ---------------------------
const tiposMulta: TipoMulta[] = ['Leve', 'Grave', 'Reincidente'];
const tiposCausa: TipoCausa[] = [
  'Infracción de Transito',
  'Acta de hechos',
  'Comparendo o citatorio',
  'Acuerdo conciliatorio o mediación',
];
const estadosCausa: EstadoCausa[] = [
  'Abierta',
  'En revisión',
  'Archivada / Cerrada',
];

const faltasCivicas = [
  {
    categoria: 'Infracciones al orden público',
    items: [
      'Alterar el orden en la vía pública',
      'Participar en riñas o peleas callejeras',
      'Proferir insultos o amenazas en lugares públicos',
      'Realizar actos obscenos en la vía pública',
      'Obstaculizar u oponerse a la autoridad en cumplimiento de sus funciones',
    ],
  },
  {
    categoria: 'Infracciones a la seguridad vial',
    items: [
      'Conducir en estado de ebriedad',
      'Conducir bajo el influjo de sustancias prohibidas',
      'Exceder los límites de velocidad establecidos',
      'Conducir sin licencia vigente',
      'Uso indebido de la vía pública (bloqueos, carreras no autorizadas)',
    ],
  },
  {
    categoria: 'Infracciones contra el medio ambiente y el entorno urbano',
    items: [
      'Tirar basura en lugares no autorizados',
      'Abandonar escombros o desechos voluminosos en vía pública',
      'Dañar o destruir mobiliario urbano (bancas, luminarias, señalización)',
      'Pintar o rayar paredes y estructuras sin autorización (grafiti)',
      'Hacer fogatas o quemas no autorizadas en espacios públicos',
    ],
  },
  {
    categoria: 'Infracciones relacionadas con la convivencia ciudadana',
    items: [
      'Consumir bebidas alcohólicas en vía pública',
      'Consumir sustancias prohibidas en espacios públicos',
      'Generar ruido excesivo que altere la tranquilidad pública',
      'Realizar eventos sin el permiso correspondiente',
      'Portar armas blancas o de fuego sin autorización',
    ],
  },
  {
    categoria: 'Infracciones contra la propiedad',
    items: [
      'Ocupación indebida de espacios públicos',
      'Colocar objetos, anuncios o mercancías que obstruyan el paso peatonal o vehicular',
      'Daño a bienes públicos o privados',
      'Sustracción no autorizada de bienes municipales',
    ],
  },
];

// ---------------------------
// Helpers dot-path para valores anidados
// ---------------------------
type PathEvt = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>;
// Devuelve "YYYY-MM-DD" para <input type="date">
export function toDateInputValue(dt?: string | Date | null): string {
  if (!dt) return '';
  const d = parseToLocalDate(dt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Devuelve "YYYY-MM-DDTHH:mm" para <input type="datetime-local">
export function toDateTimeLocalInputValue(dt?: string | Date | null): string {
  if (!dt) return '';
  const d = parseToLocalDate(dt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

// Parser robusto: si viene con "Z" u offset lo respeta; si viene sin zona, lo trata como local
function parseToLocalDate(dt: string | Date): Date {
  if (dt instanceof Date) return dt;
  // ISO con Z u offset (ej. 2025-08-19T13:22:00Z o 2025-08-19T13:22:00-05:00)
  if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(dt)) return new Date(dt);
  // Solo fecha (YYYY-MM-DD) o sin zona → interprétalo como local
  // Para "YYYY-MM-DD" mantenemos medianoche local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dt)) {
    const [y, m, d] = dt.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  // "YYYY-MM-DDTHH:mm" sin zona → tómalo como local
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dt)) {
    const [datePart, timePart] = dt.split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = timePart.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0);
  }
  // Fallback
  return new Date(dt);
}

// Ya los tenías, los dejo aquí por claridad:

// ⬇️ pon esto junto a los helpers
const nowDate = () => {
  const d = new Date();
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000); // normaliza para input date
  return tz.toISOString().slice(0, 10); // YYYY-MM-DD
};
const nowDateTimeLocal = () => {
  const d = new Date();
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
};

const setDeep = (obj: any, path: string, value: any) => {
  const keys = path.split('.');
  const last = keys.pop()!;
  const target = keys.reduce((acc, key) => {
    if (acc[key] == null || typeof acc[key] !== 'object') acc[key] = {};
    return acc[key];
  }, obj);
  target[last] = value;
};

const parseTipoLicencia = (s?: string) => {
  if (!s) return undefined;
  const ambito = s.startsWith('FED') ? 'FED' : 'EST-TLX';
  const tipo = s.replace(/^.*?:\s*/, ''); // quita "FED: " o "EST-TLX: "
  // si tu backend espera otro tipo exacto, ajústalo aquí
  return { ambito, tipo } as any;
};

// ---------------------------
// Props
// ---------------------------
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGuardar: (c: CausaJudicial) => void;
  causa?: CausaJudicial | null;
  ultimoExpediente?: number;
}

// ---------------------------
// Componente
// ---------------------------
export default function ModalCausa({
  isOpen,
  onClose,
  onGuardar,
  causa,
  ultimoExpediente = 0,
}: Props) {
  const toast = useToast();
  const [form, setForm] = useState<Partial<CausaJudicial>>({});
  const editorRef = useRef<HTMLDivElement | null>(null);
  const refFotos = useRef<HTMLInputElement | null>(null);
  const refPdfs = useRef<HTMLInputElement | null>(null);
  const refActa = useRef<HTMLInputElement | null>(null);

  const fotosId = useId();
  const pdfsId = useId();
  const actaId = useId();
  // Google Maps refs
  const [mapKey, setMapKey] = useState(0);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const mapaDivRef = useRef<HTMLDivElement | null>(null);

  // ---------------------------
  // Google Maps helpers
  // ---------------------------
  function loadGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {
      const anyWin = window as any;
      if (anyWin.google?.maps) return resolve();

      const existing = document.getElementById('gmaps-sdk');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () =>
          reject(new Error('Error loading GMAPS')),
        );
        return;
      }
      const script = document.createElement('script');
      script.id = 'gmaps-sdk';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Error loading GMAPS'));
      document.body.appendChild(script);
    });
  }

  function waitForVisible(
    elRef: React.RefObject<HTMLElement>,
    tries = 40,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const tick = () => {
        const el = elRef.current;
        if (el && el.offsetWidth > 0 && el.offsetHeight > 0) {
          resolve();
        } else if (tries <= 0) {
          reject(new Error('Map container never became visible'));
        } else {
          requestAnimationFrame(() => {
            waitForVisible(elRef, tries - 1)
              .then(resolve)
              .catch(reject);
          });
        }
      };
      tick();
    });
  }

  const putMarker = (pos: { lat: number; lng: number }) => {
    if (!mapRef.current) return;
    if (!markerRef.current) {
      markerRef.current = new google.maps.Marker({
        map: mapRef.current,
        position: pos,
        draggable: true,
      });
      markerRef.current.addListener(
        'dragend',
        (ev: google.maps.MapMouseEvent) => {
          if (!ev.latLng) return;
          const newPos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
          setForm((prev) => ({
            ...prev,
            lugarHecho: {
              ...(prev.lugarHecho || {}),
              lat: newPos.lat,
              lng: newPos.lng,
            },
          }));
        },
      );
    } else {
      markerRef.current.setPosition(pos);
    }
  };

  // ---------------------------
  // Efectos
  // ---------------------------
 useEffect(() => {
  if (causa) {
    const esDenuncia = causa.tipoCausa === 'Denuncia ciudadana';
    const incoming = causa.fechaIngreso;

    setForm({
      ...causa,
      fechaIngreso: esDenuncia
        ? toDateTimeLocalInputValue(incoming || nowDateTimeLocal())
        : toDateInputValue(incoming || nowDate()),
      registrarTestigos: !!(causa.testigos && causa.testigos.length > 0),
      evidencias: causa.evidencias || {},
    });
  } else {
    // nuevo registro: default hoy
    const esDenuncia = form.tipoCausa === 'Denuncia ciudadana';
    setForm({
      ...form,
      fechaIngreso: esDenuncia ? nowDateTimeLocal() : nowDate(),
      registrarTestigos: false,
      evidencias: {},
    });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [causa]);


  useEffect(() => {
    if (isOpen && !causa) {
      setForm((prev) => ({
        ...prev,
        fechaIngreso:
          prev.fechaIngreso ||
          (prev.tipoCausa === 'Denuncia ciudadana'
            ? nowDateTimeLocal()
            : nowDate()),
      }));
    }
  }, [isOpen, causa]);

  useEffect(() => {
    if (isOpen && form.tipoCausa === 'Acta de hechos') {
      setMapKey((k) => k + 1); // fuerza remount del contenedor del mapa
    }
  }, [isOpen, form.tipoCausa]);

  useEffect(() => {
    const esActaDeHechos = form.tipoCausa === 'Acta de hechos';
    if (!isOpen || !esActaDeHechos) return;

    let cancelled = false;

    (async () => {
      try {
        await loadGoogleMaps();
        await waitForVisible(mapaDivRef);

        if (cancelled) return;

        const lat = form.lugarHecho?.lat ?? 19.4326;
        const lng = form.lugarHecho?.lng ?? -99.1332;

        if (!mapRef.current) {
          mapRef.current = new google.maps.Map(
            mapaDivRef.current as HTMLDivElement,
            {
              zoom: 15,
              center: { lat, lng },
            },
          );

          mapRef.current.addListener(
            'click',
            (ev: google.maps.MapMouseEvent) => {
              if (!ev.latLng) return;
              const pos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
              putMarker(pos);
              setForm((prev) => ({
                ...prev,
                lugarHecho: {
                  ...(prev.lugarHecho || {}),
                  lat: pos.lat,
                  lng: pos.lng,
                },
              }));
            },
          );
        }

        const pos = { lat, lng };
        putMarker(pos);
        mapRef.current.setCenter(pos);

        setTimeout(() => {
          if (!mapRef.current) return;
          // @ts-ignore
          google.maps.event.trigger(mapRef.current, 'resize');
          mapRef.current.setCenter(pos);
        }, 220);
      } catch {
        if (mapaDivRef.current) {
          mapaDivRef.current.innerHTML =
            'No fue posible cargar Google Maps (verifica tu API Key).';
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    form.tipoCausa,
    form.lugarHecho?.lat,
    form.lugarHecho?.lng,
    mapKey,
  ]);

  // Autogenerar número de acta si es "Acta de hechos"
  useEffect(() => {
    if (form.tipoCausa === 'Acta de hechos' && !form.numeroActa) {
      const folio = `ACT-${(ultimoExpediente + 1).toString().padStart(6, '0')}`;
      setForm((prev) => ({ ...prev, numeroActa: folio }));
    }
  }, [form.tipoCausa, ultimoExpediente]);

  // ---------------------------
  // Handlers
  // ---------------------------
  const handleChange = (e: PathEvt) => {
    const { name, value, files, type, checked } = e.target as any;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (files) {
      const isMultiple = (e.target as HTMLInputElement).multiple;
      if (isMultiple) {
        setForm((prev) => ({
          ...prev,
          evidencias: {
            ...(prev.evidencias || {}),
            [name]: Array.from(files),
          },
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          evidencias: {
            ...(prev.evidencias || {}),
            [name]: files[0],
          },
        }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleChangePath = (e: PathEvt) => {
    const { name, type } = e.target as any;
    const value =
      type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : e.target.value;
    setForm((prev) => {
      const next: any = { ...prev };
      setDeep(next, name, value);
      return next;
    });
  };

  const commitDescripcionHtml = () => {
    const html = editorRef.current?.innerHTML ?? '';
    setForm((prev) => ({ ...prev, descripcionDetalladaHtml: html }));
  };

  // Sanciones (array)
  const addSancion = () => {
    setForm((prev) => ({
      ...prev,
      sanciones: [
        ...(prev.sanciones || []),
        { numero: '', normativa: '', fundamento: '', observaciones: '' },
      ],
    }));
  };
  const removeSancion = (idx: number) => {
    setForm((prev) => {
      const next = [...(prev.sanciones || [])];
      next.splice(idx, 1);
      return { ...prev, sanciones: next };
    });
  };
  const handleSancionChange = (
    idx: number,
    field: 'numero' | 'normativa' | 'fundamento' | 'observaciones',
    value: string,
  ) => {
    setForm((prev) => {
      const next = [...(prev.sanciones || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, sanciones: next };
    });
  };

  // Testigos / Involucrados
  const addTestigo = () => {
    setForm((prev) => ({
      ...prev,
      testigos: [...(prev.testigos || []), { nombreCompleto: '' } as Testigo],
    }));
  };
  const updateTestigo = (idx: number, field: keyof Testigo, value: string) => {
    setForm((prev) => {
      const arr = [...(prev.testigos || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, testigos: arr };
    });
  };
  const removeTestigo = (idx: number) => {
    setForm((prev) => {
      const arr = [...(prev.testigos || [])];
      arr.splice(idx, 1);
      return { ...prev, testigos: arr };
    });
  };

  const addInvolucrado = () => {
    setForm((prev) => ({
      ...prev,
      involucrados: [
        ...(prev.involucrados || []),
        { nombreCompleto: '' } as Involucrado,
      ],
    }));
  };
  const updateInvolucrado = (
    idx: number,
    field: keyof Involucrado,
    value: string,
  ) => {
    setForm((prev) => {
      const arr = [...(prev.involucrados || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, involucrados: arr };
    });
  };
  const removeInvolucrado = (idx: number) => {
    setForm((prev) => {
      const arr = [...(prev.involucrados || [])];
      arr.splice(idx, 1);
      return { ...prev, involucrados: arr };
    });
  };

  // ---------------------------
  // Flags de UI
  // ---------------------------
  const archivosExistentes = useMemo(() => {
  // Según cómo llegue desde tu backend:
  // - Prisma sin transformar ⇒ "CausaArchivo"
  // - Si camelCaseaste en algún lado ⇒ "causaArchivo"
  const arrRaw =
    (causa as any)?.CausaArchivo ??
    (causa as any)?.causaArchivo ??
    [];

  const arr: CausaArchivoRow[] = Array.isArray(arrRaw) ? arrRaw : [];

  // Para depurar sin bloquear la UI
  console.log('CausaArchivo length:', arr.length, 'causaId:', (causa as any)?.id);
    console.log('CausaArchivo data:', causa);
  const fotos   = arr.filter(a => a.mime?.startsWith('image/') || a.tipo === 'FOTO');
  const actas   = arr.filter(a => a.tipo === 'ACTA_INFRACCION' && a.mime === 'application/pdf');
  const ordenes = arr.filter(a => a.tipo === 'ORDEN_GRUA');
  const pdfs    = arr.filter(a => a.mime === 'application/pdf' && a.tipo === 'PDF');

  return { fotos, actas, ordenes, pdfs, all: arr };
}, [ (causa as any)?.CausaArchivo, (causa as any)?.causaArchivo, (causa as any)?.id ]);


  const mostrarInfraccionOMulta = useMemo(
    () =>
      form.tipoCausa === 'Infracción de Transito' || form.tipoCausa === 'Multa',
    [form.tipoCausa],
  );
  const esActaDeHechos = form.tipoCausa === 'Acta de hechos';
  const esDenuncia = form.tipoCausa === 'Denuncia ciudadana';

  // ---------------------------
  // Guardar
  // ---------------------------
  const handleGuardar = () => {
    commitDescripcionHtml();

    const faltanTipo = !form.tipoCausa;
    const faltanDescripcion =
      !form.descripcionDetalladaHtml ||
      form.descripcionDetalladaHtml.trim() === '';
    const faltaFechaOYFaltaCivica = !form.fechaIngreso && !form.faltaCivica;

    if (faltanTipo || faltanDescripcion || faltaFechaOYFaltaCivica) {
      const msgs: string[] = [];
      if (faltanTipo) msgs.push('tipo de causa');
      if (faltanDescripcion) msgs.push('descripción de los hechos');
      if (faltaFechaOYFaltaCivica) msgs.push('fecha de ingreso o falta cívica');
      toast({
        title: 'Campos incompletos',
        description: `Faltan: ${msgs.join(', ')}`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Construir payload
    const nuevo: CausaJudicial = {
      id: causa?.id || 0,
      expediente:
        causa?.expediente ||
        `EXP-${(ultimoExpediente + 1).toString().padStart(4, '0')}`,
      tipoCausa: form.tipoCausa as TipoCausa,
      estado: (form.estado || 'Abierta') as EstadoCausa,

      fechaIngreso: form.fechaIngreso,
      horaIngreso: form.horaIngreso,

      infractor: form.infractor,
      denunciante: form.denunciante, // no aplica en “infracción de tránsito” pero lo conservamos

      normativa: form.normativa,
      descripcionDetalladaHtml: form.descripcionDetalladaHtml || '',
      observaciones: form.observaciones,

      tipoMulta: form.tipoMulta as any,
      pagosMulta: form.pagosMulta ? Number(form.pagosMulta) : undefined,
      descuento: form.descuento ? Number(form.descuento) : undefined,
      condonacionTotal: !!form.condonacionTotal,
      ordenadoPor: form.ordenadoPor,

      faltaCivica: form.faltaCivica,
      nomenclatura: form.nomenclatura,

      registrarTestigos: !!form.registrarTestigos,
      testigos: form.registrarTestigos ? form.testigos || [] : [],

      evidencias: form.evidencias || {},

      // Acta de hechos
      numeroActa: esActaDeHechos ? form.numeroActa : undefined,
      fechaHoraLevantamiento: esActaDeHechos
        ? form.fechaHoraLevantamiento
        : undefined,
      funcionarioQueLevanta: esActaDeHechos
        ? form.funcionarioQueLevanta
        : undefined,
      involucrados: esActaDeHechos ? form.involucrados || [] : undefined,
      lugarHecho: esActaDeHechos ? form.lugarHecho || undefined : undefined,

      // Infracción de tránsito
      conductor: mostrarInfraccionOMulta
        ? form.conductor
          ? {
              ...form.conductor,
              // convierte el string del select a objeto si tu tipo lo requiere
              // si tu backend solo guarda string, deja tipoLicenciaStr en conductor y omite esto
              // @ts-ignore
              tipoLicencia: parseTipoLicencia(
                (form.conductor as any).tipoLicenciaStr ??
                  (form as any).tipoLicenciaStr,
              ),
            }
          : undefined
        : undefined,

      lugarInfraccion: mostrarInfraccionOMulta
        ? form.lugarInfraccion
        : undefined,

      garantia: mostrarInfraccionOMulta ? (form.garantia as any) : undefined,
      usarMismosDatosVehiculo: mostrarInfraccionOMulta
        ? !!form.usarMismosDatosVehiculo
        : undefined,

      vehiculo: mostrarInfraccionOMulta
        ? form.vehiculo || undefined
        : undefined,
      propietario: mostrarInfraccionOMulta
        ? form.propietario || undefined
        : undefined,

      sanciones: mostrarInfraccionOMulta ? form.sanciones || [] : undefined,

      // Para archivo, aquí idealmente guardas la URL tras subirlo: form.ordenGruaPdf es File (frontend)
      // puedes transformar antes de enviar
      // @ts-ignore
      ordenGruaPdf: mostrarInfraccionOMulta
        ? (form as any).ordenGruaPdfPath ?? undefined
        : undefined,

      elementoPolicialNombre: mostrarInfraccionOMulta
        ? form.elementoPolicialNombre
        : undefined,
      elementoPolicialCredencial: mostrarInfraccionOMulta
        ? form.elementoPolicialCredencial
        : undefined,

      montoSancion:
        mostrarInfraccionOMulta && form.montoSancion != null
          ? Number(form.montoSancion)
          : undefined,
    };

    onGuardar(nuevo);
    onClose();
  };

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          fontSize="2xl" 
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          borderTopRadius="md"
          position="relative"
        >
          {causa ? 'Editar' : 'Nueva'} Causa
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Fecha ingreso */}
            <FormControl isRequired={!form.faltaCivica}>
              <FormLabel>
                {esDenuncia ? 'Fecha de registro' : 'Fecha de ingreso'}
              </FormLabel>
       {/* No denuncia → fecha */}
<Input
  type="date"
  name="fechaIngreso"
  value={form.fechaIngreso || ''} // ← ya viene YYYY-MM-DD
  onChange={handleChange}
/>

{/* Denuncia → datetime-local */}
{/* <Input
  type="datetime-local"
  name="fechaIngreso"
  value={form.fechaIngreso || ''} // ← ya viene YYYY-MM-DDTHH:mm
  onChange={handleChange}
/> */}

            </FormControl>

            {esDenuncia && (
              <FormControl>
                <FormLabel>Hora (opcional si usas datetime-local)</FormLabel>
                <Input
                  type="time"
                  name="horaIngreso"
                  value={form.horaIngreso || ''}
                  onChange={handleChange}
                />
              </FormControl>
            )}

            {/* Tipo de causa */}
            <FormControl isRequired>
              <FormLabel>Tipo de causa</FormLabel>
              <Select
                name="tipoCausa"
                value={form.tipoCausa || ''}
                onChange={handleChange}
              >
                <option value="">Seleccione...</option>
                {tiposCausa.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Estado */}
            <FormControl>
              <FormLabel>Estado</FormLabel>
              <Select
                name="estado"
                value={form.estado || 'Abierta'}
                onChange={handleChange}
              >
                {estadosCausa.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Infractor / Denunciante */}
            <FormControl isDisabled={esActaDeHechos}>
              <FormLabel>Infractor</FormLabel>
              <Input
                name="infractor"
                value={form.infractor || ''}
                onChange={handleChange}
              />
            </FormControl>

            {!mostrarInfraccionOMulta && (
              <FormControl isDisabled={esActaDeHechos}>
                <FormLabel>Denunciante (opcional)</FormLabel>
                <Input
                  name="denunciante"
                  value={form.denunciante || ''}
                  onChange={handleChange}
                />
              </FormControl>
            )}

            {/* Sección: Infracción de tránsito / Multa */}
            {mostrarInfraccionOMulta && (
              <>
                {/* Folio & Nomenclatura */}
                <FormControl isRequired>
                  <FormLabel>Folio No.</FormLabel>
                  <Input
                    name="folio"
                    value={form.folio || ''}
                    onChange={handleChange}
                    placeholder="Ej. ABC-2025-001"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Nomenclatura</FormLabel>
                  <Input
                    name="nomenclatura"
                    value={form.nomenclatura || ''}
                    onChange={handleChange}
                    placeholder="Ej. PC/DR000/TOTOLAC/2025"
                  />
                </FormControl>

                {/* Conductor */}
                <FormControl>
                  <FormLabel>Conductor — Apellido Paterno</FormLabel>
                  <Input
                    name="conductor.apellidoPaterno"
                    value={form.conductor?.apellidoPaterno ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Conductor — Apellido Materno</FormLabel>
                  <Input
                    name="conductor.apellidoMaterno"
                    value={form.conductor?.apellidoMaterno ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Conductor — Nombre(s)</FormLabel>
                  <Input
                    name="conductor.nombres"
                    value={form.conductor?.nombres ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>

                {/* Domicilio conductor */}
                <FormControl>
                  <FormLabel>Conductor — Calle y número</FormLabel>
                  <Input
                    name="conductor.domicilio.calleNumero"
                    value={form.conductor?.domicilio?.calleNumero ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Conductor — Colonia</FormLabel>
                  <Input
                    name="conductor.domicilio.colonia"
                    value={form.conductor?.domicilio?.colonia ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Conductor — Municipio</FormLabel>
                  <Input
                    name="conductor.domicilio.municipio"
                    value={form.conductor?.domicilio?.municipio ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Conductor — Estado</FormLabel>
                  <Input
                    name="conductor.domicilio.estado"
                    value={form.conductor?.domicilio?.estado ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Conductor — C.P.</FormLabel>
                  <Input
                    type="number"
                    name="conductor.domicilio.cp"
                    value={form.conductor?.domicilio?.cp ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>

                {/* Licencia */}
                <FormControl>
                  <FormLabel>No. Licencia</FormLabel>
                  <Input
                    name="conductor.numeroLicencia"
                    value={form.conductor?.numeroLicencia ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Vigencia — Inicio</FormLabel>
                  <Input
                    type="date"
                    name="conductor.vigenciaInicio"
                    value={form.conductor?.vigenciaInicio ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Vigencia — Fin</FormLabel>
                  <Input
                    type="date"
                    name="conductor.vigenciaFin"
                    value={form.conductor?.vigenciaFin ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Tipo de licencia</FormLabel>
                  <Select
                    name="conductor.tipoLicenciaStr" // <- string plano
                    value={(form as any).conductor?.tipoLicenciaStr ?? ''}
                    onChange={handleChangePath}
                  >
                    <option value="">Seleccione...</option>
                    <optgroup label="Licencias Federales">
                      {LICENCIAS_FEDERALES.map((t: string) => (
                        <option key={t} value={`FED: ${t}`}>
                          {t}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Licencia Estatal (Tlaxcala)">
                      {LICENCIAS_ESTAT_TLX.map((t: string) => (
                        <option key={t} value={`EST-TLX: ${t}`}>
                          {t}
                        </option>
                      ))}
                    </optgroup>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Entidad de licencia</FormLabel>
                  <Select
                    name="conductor.entidadLicencia"
                    value={form.conductor?.entidadLicencia ?? ''}
                    onChange={handleChangePath}
                  >
                    <option value="">Seleccione...</option>
                    {ENTIDADES_MX.map((e: EntidadFederativa) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Lugar de la infracción */}
                <FormControl isRequired>
                  <FormLabel>Lugar donde se cometió la infracción</FormLabel>
                  <Input
                    name="lugarInfraccion"
                    value={form.lugarInfraccion || ''}
                    onChange={handleChange}
                  />
                </FormControl>

                {/* Garantía */}
                <FormControl>
                  <FormLabel>Garantía</FormLabel>
                  <Select
                    name="garantia"
                    value={(form.garantia as any) || ''}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione...</option>
                    <option value="Licencia">Licencia de conducir</option>
                    <option value="Placas">Placas</option>
                    <option value="Vehículo">Vehículo</option>
                  </Select>
                </FormControl>

                {/* Datos del vehículo (si garantía = Vehículo) */}
                {form.garantia === 'Vehículo' && (
                  <>
                    <FormControl mt={2}>
                      <Checkbox
                        name="usarMismosDatosVehiculo"
                        isChecked={!!form.usarMismosDatosVehiculo}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            usarMismosDatosVehiculo: e.target.checked,
                          }))
                        }
                      >
                        Usar los mismos datos de vehículo
                      </Checkbox>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Vehículo — Marca</FormLabel>
                      <Input
                        name="vehiculo.marca"
                        value={form.vehiculo?.marca ?? ''}
                        onChange={handleChangePath}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Vehículo — Tipo</FormLabel>
                      <Select
                        name="vehiculo.tipo"
                        value={form.vehiculo?.tipo ?? ''}
                        onChange={handleChangePath}
                      >
                        <option value="">Seleccione...</option>
                        {TIPOS_VEHICULO.map((tv: string) => (
                          <option key={tv} value={tv}>
                            {tv}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Vehículo — Año / Modelo</FormLabel>
                      <Input
                        name="vehiculo.anioModelo"
                        value={form.vehiculo?.anioModelo ?? ''}
                        onChange={handleChangePath}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Vehículo — No. Motor</FormLabel>
                      <Input
                        name="vehiculo.numeroMotor"
                        value={form.vehiculo?.numeroMotor ?? ''}
                        onChange={handleChangePath}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Vehículo — No. Serie</FormLabel>
                      <Input
                        name="vehiculo.numeroSerie"
                        value={form.vehiculo?.numeroSerie ?? ''}
                        onChange={handleChangePath}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Vehículo — Placas</FormLabel>
                      <Input
                        name="vehiculo.placas"
                        value={form.vehiculo?.placas ?? ''}
                        onChange={handleChangePath}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Vehículo — Entidad de placas</FormLabel>
                      <Select
                        name="vehiculo.entidadPlacas"
                        value={form.vehiculo?.entidadPlacas ?? ''}
                        onChange={handleChangePath}
                      >
                        <option value="">Seleccione...</option>
                        {ENTIDADES_MX.map((e: EntidadFederativa) => (
                          <option key={e} value={e}>
                            {e}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {/* Propietario (opcional) */}
                <FormControl>
                  <FormLabel>Propietario — Apellido Paterno</FormLabel>
                  <Input
                    name="propietario.apellidoPaterno"
                    value={form.propietario?.apellidoPaterno ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Propietario — Apellido Materno</FormLabel>
                  <Input
                    name="propietario.apellidoMaterno"
                    value={form.propietario?.apellidoMaterno ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Propietario — Nombre(s)</FormLabel>
                  <Input
                    name="propietario.nombres"
                    value={form.propietario?.nombres ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Propietario — Calle y número</FormLabel>
                  <Input
                    name="propietario.domicilio.calleNumero"
                    value={form.propietario?.domicilio?.calleNumero ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Propietario — Colonia</FormLabel>
                  <Input
                    name="propietario.domicilio.colonia"
                    value={form.propietario?.domicilio?.colonia ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Propietario — Municipio</FormLabel>
                  <Input
                    name="propietario.domicilio.municipio"
                    value={form.propietario?.domicilio?.municipio ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Propietario — Estado</FormLabel>
                  <Input
                    name="propietario.domicilio.estado"
                    value={form.propietario?.domicilio?.estado ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Propietario — C.P.</FormLabel>
                  <Input
                    type="number"
                    name="propietario.domicilio.cp"
                    value={form.propietario?.domicilio?.cp ?? ''}
                    onChange={handleChangePath}
                  />
                </FormControl>

                {/* Sanciones (múltiples) */}
                <FormControl>
                  <FormLabel>Sanciones</FormLabel>
                  <Button size="sm" onClick={addSancion}>
                    Agregar sanción
                  </Button>
                </FormControl>

                {(form.sanciones || []).map((s: any, idx: number) => (
                  <Box key={idx} p={3} borderWidth="1px" borderRadius="md">
                    <FormControl>
                      <FormLabel>Número</FormLabel>
                      <Input
                        value={s.numero || ''}
                        onChange={(e) =>
                          handleSancionChange(idx, 'numero', e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Normativa infringida</FormLabel>
                      <Input
                        value={s.normativa || ''}
                        onChange={(e) =>
                          handleSancionChange(idx, 'normativa', e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Fundamento legal</FormLabel>
                      <Textarea
                        value={s.fundamento || ''}
                        onChange={(e) =>
                          handleSancionChange(idx, 'fundamento', e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Observaciones</FormLabel>
                      <Textarea
                        value={s.observaciones || ''}
                        onChange={(e) =>
                          handleSancionChange(
                            idx,
                            'observaciones',
                            e.target.value,
                          )
                        }
                      />
                    </FormControl>
                    <Button
                      mt={2}
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={() => removeSancion(idx)}
                    >
                      Quitar
                    </Button>
                  </Box>
                ))}

                {/* Orden de grúa PDF */}
                <FormControl>
                  <FormLabel>Orden de grúa (PDF)</FormLabel>
                  <Input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) =>
                      setForm(
                        (prev) =>
                          ({
                            ...prev,
                            /* guarda temporalmente el File */ ordenGruaPdf:
                              (e.target as HTMLInputElement).files?.[0] || null,
                          } as any),
                      )
                    }
                  />
                </FormControl>

                {/* Elemento policial */}
                <FormControl>
                  <FormLabel>Elemento policial — Nombre</FormLabel>
                  <Input
                    name="elementoPolicialNombre"
                    value={form.elementoPolicialNombre || ''}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Elemento policial — No. credencial</FormLabel>
                  <Input
                    name="elementoPolicialCredencial"
                    value={form.elementoPolicialCredencial || ''}
                    onChange={handleChange}
                  />
                </FormControl>

                {/* Monto */}
                <FormControl>
                  <FormLabel>Monto de la sanción ($)</FormLabel>
                  <Input
                    type="number"
                    step="1"
                    name="montoSancion"
                    value={(form.montoSancion as any) ?? ''}
                    onChange={handleChange}
                  />
                </FormControl>
              </>
            )}

            {/* Acta de hechos */}
            {esActaDeHechos && (
              <>
                <FormControl isRequired>
                  <FormLabel>Número de acta (folio único)</FormLabel>
                  <Input
                    name="numeroActa"
                    value={form.numeroActa || ''}
                    onChange={handleChange}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Fecha y hora de levantamiento</FormLabel>
                  <Input
                    type="datetime-local"
                    name="fechaHoraLevantamiento"
                    value={form.fechaHoraLevantamiento || ''}
                    onChange={handleChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Nomenclatura</FormLabel>
                  <Input
                    name="nomenclatura"
                    value={form.nomenclatura || ''}
                    onChange={handleChange}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Funcionario que levanta el acta</FormLabel>
                  <Input
                    name="funcionarioQueLevanta"
                    value={form.funcionarioQueLevanta || ''}
                    onChange={handleChange}
                  />
                </FormControl>

                {/* Lugar del hecho */}
                <Box gridColumn={{ md: '1 / -1' }}>
                  <FormLabel>Lugar del hecho</FormLabel>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <Input
                      placeholder="Dirección exacta"
                      name="lugarHecho.direccion"
                      value={form.lugarHecho?.direccion || ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          lugarHecho: {
                            ...(prev.lugarHecho || {}),
                            direccion: e.target.value,
                          },
                        }))
                      }
                    />
                    <Input
                      placeholder="Colonia"
                      name="lugarHecho.colonia"
                      value={form.lugarHecho?.colonia || ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          lugarHecho: {
                            ...(prev.lugarHecho || {}),
                            colonia: e.target.value,
                          },
                        }))
                      }
                    />
                    <Input
                      placeholder="Referencias"
                      name="lugarHecho.referencias"
                      value={form.lugarHecho?.referencias || ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          lugarHecho: {
                            ...(prev.lugarHecho || {}),
                            referencias: e.target.value,
                          },
                        }))
                      }
                    />
                  </SimpleGrid>
                  <HStack mt={2} spacing={3}>
                    <Input
                      type="number"
                      step="any"
                      placeholder="Latitud"
                      value={form.lugarHecho?.lat ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          lugarHecho: {
                            ...(prev.lugarHecho || {}),
                            lat: e.target.value ? Number(e.target.value) : null,
                          },
                        }))
                      }
                    />
                    <Input
                      type="number"
                      step="any"
                      placeholder="Longitud"
                      value={form.lugarHecho?.lng ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          lugarHecho: {
                            ...(prev.lugarHecho || {}),
                            lng: e.target.value ? Number(e.target.value) : null,
                          },
                        }))
                      }
                    />
                  </HStack>
                  <Box
                    key={`map-${mapKey}`}
                    ref={mapaDivRef}
                    id="mapa-acta-hecho"
                    mt={3}
                    h="300px"
                    w="100%"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  />
                </Box>

                {/* Involucrados */}
                <Box gridColumn={{ md: '1 / -1' }}>
                  <HStack justify="space-between">
                    <FormLabel>Involucrados</FormLabel>
                    <IconButton
                      aria-label="Agregar involucrado"
                      icon={<AddIcon />}
                      size="sm"
                      onClick={addInvolucrado}
                    />
                  </HStack>
                  <VStack spacing={3} align="stretch">
                    {(form.involucrados || []).map((inv, idx) => (
                      <Box
                        key={idx}
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <HStack>
                          <Input
                            placeholder="Nombre completo"
                            value={inv.nombreCompleto}
                            onChange={(e) =>
                              updateInvolucrado(
                                idx,
                                'nombreCompleto',
                                e.target.value,
                              )
                            }
                          />
                          <IconButton
                            aria-label="Quitar"
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => removeInvolucrado(idx)}
                          />
                        </HStack>
                        <SimpleGrid
                          columns={{ base: 1, md: 3 }}
                          spacing={2}
                          mt={2}
                        >
                          <Input
                            placeholder="Domicilio"
                            value={inv.domicilio || ''}
                            onChange={(e) =>
                              updateInvolucrado(
                                idx,
                                'domicilio',
                                e.target.value,
                              )
                            }
                          />
                          <Input
                            placeholder="Teléfono"
                            value={inv.telefono || ''}
                            onChange={(e) =>
                              updateInvolucrado(idx, 'telefono', e.target.value)
                            }
                          />
                          <Input
                            placeholder="Correo"
                            value={inv.correo || ''}
                            onChange={(e) =>
                              updateInvolucrado(idx, 'correo', e.target.value)
                            }
                          />
                        </SimpleGrid>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </>
            )}

            {/* Editor HTML */}
            <Box gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Descripción detallada de los hechos</FormLabel>
              <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                minH="160px"
                p={3}
                border="1px solid"
                borderColor="gray.300"
                borderRadius="md"
                onBlur={commitDescripcionHtml}
                dangerouslySetInnerHTML={{
                  __html: form.descripcionDetalladaHtml || '',
                }}
              />
              <Box mt={2} fontSize="sm" color="gray.600">
                Tip: puedes pegar texto con formato; se guardará en HTML.
                Adjunta imágenes como evidencias si lo prefieres.
              </Box>
            </Box>

            {/* Testigos */}
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <Checkbox
                name="registrarTestigos"
                isChecked={!!form.registrarTestigos}
                onChange={handleChange}
              >
                Registrar testigos
              </Checkbox>
              {form.registrarTestigos && (
                <Box mt={2}>
                  <HStack justify="space-between">
                    <FormLabel m={0}>Testigos</FormLabel>
                    <IconButton
                      aria-label="Agregar testigo"
                      icon={<AddIcon />}
                      size="sm"
                      onClick={addTestigo}
                    />
                  </HStack>
                  <VStack spacing={3} align="stretch">
                    {(form.testigos || []).map((t, idx) => (
                      <Box
                        key={idx}
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2}>
                          <Input
                            placeholder="Nombre completo"
                            value={t.nombreCompleto}
                            onChange={(e) =>
                              updateTestigo(
                                idx,
                                'nombreCompleto',
                                e.target.value,
                              )
                            }
                          />
                          <Input
                            placeholder="Folio INE"
                            value={(t as any).folioINE || ''}
                            onChange={(e) =>
                              updateTestigo(
                                idx,
                                'folioINE' as any,
                                e.target.value,
                              )
                            }
                          />
                          <Input
                            placeholder="CURP"
                            value={(t as any).curp || ''}
                            onChange={(e) =>
                              updateTestigo(idx, 'curp' as any, e.target.value)
                            }
                          />
                          <HStack>
                            <Input
                              placeholder="Teléfono"
                              value={(t as any).telefono || ''}
                              onChange={(e) =>
                                updateTestigo(
                                  idx,
                                  'telefono' as any,
                                  e.target.value,
                                )
                              }
                            />
                            <IconButton
                              aria-label="Quitar"
                              icon={<DeleteIcon />}
                              size="sm"
                              onClick={() => removeTestigo(idx)}
                            />
                          </HStack>
                        </SimpleGrid>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </FormControl>

            <Box gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Evidencias</FormLabel>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                {/* FOTOS */}
                <FormControl>
                  <FormLabel>Fotografías (múltiples)</FormLabel>

                  {/* Input nativo fuera de vista (pero no display:none). Offscreen. */}
                  <input
                    id={fotosId}
                    name="fotos"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleChange}
                    // offscreen visible para el navegador (evita bloqueos de Safari/iOS)
                    style={{
                      position: 'fixed',
                      top: '-10000px',
                      left: '-10000px',
                      opacity: 0,
                      width: 0,
                      height: 0,
                    }}
                  />

                  <Button
                    as="label"
                    htmlFor={fotosId}
                    cursor="pointer"
                    onClick={() => {
                      const el = document.getElementById(
                        fotosId,
                      ) as HTMLInputElement | null;
                      if (el) {
                        // abre con API moderna si está disponible (Chrome/Edge), si no, fallback a click()
                        // (ambos son gestos de usuario, no viola políticas del navegador)
                        (el as any).showPicker?.() ?? el.click();
                      }
                    }}
                  >
                    Seleccionar fotos
                  </Button>

                  {form.evidencias?.fotos?.length ? (
                    <Box mt={2} fontSize="sm">
                      {form.evidencias.fotos.length} archivo(s) seleccionado(s)
                    </Box>
                  ) : null}
                </FormControl>

                {/* PDFS */}
                <FormControl>
                  <FormLabel>Documentos PDF (múltiples)</FormLabel>

                  <input
                    id={pdfsId}
                    name="pdfs"
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={handleChange}
                    style={{
                      position: 'fixed',
                      top: '-10000px',
                      left: '-10000px',
                      opacity: 0,
                      width: 0,
                      height: 0,
                    }}
                  />

                  <Button
                    as="label"
                    htmlFor={pdfsId}
                    cursor="pointer"
                    onClick={() => {
                      const el = document.getElementById(
                        pdfsId,
                      ) as HTMLInputElement | null;
                      if (el) (el as any).showPicker?.() ?? el.click();
                    }}
                  >
                    Seleccionar PDFs
                  </Button>

                  {form.evidencias?.pdfs?.length ? (
                    <Box mt={2} fontSize="sm">
                      {(form.evidencias.pdfs as File[]).length} PDF(s)
                      seleccionado(s)
                    </Box>
                  ) : null}
                </FormControl>

                {/* ACTA */}
                <FormControl>
                  <FormLabel>Acta de infracción (PDF)</FormLabel>

                  <input
                    id={actaId}
                    name="actaInfraccionPdf"
                    type="file"
                    accept="application/pdf"
                    onChange={handleChange}
                    style={{
                      position: 'fixed',
                      top: '-10000px',
                      left: '-10000px',
                      opacity: 0,
                      width: 0,
                      height: 0,
                    }}
                  />

                  <Button
                    as="label"
                    htmlFor={actaId}
                    cursor="pointer"
                    onClick={() => {
                      const el = document.getElementById(
                        actaId,
                      ) as HTMLInputElement | null;
                      if (el) (el as any).showPicker?.() ?? el.click();
                    }}
                  >
                    Seleccionar acta
                  </Button>

                  {form.evidencias?.actaInfraccionPdf ? (
                    <Box mt={2} fontSize="sm">
                      {(form.evidencias.actaInfraccionPdf as File).name}
                    </Box>
                  ) : null}
                </FormControl>
              </SimpleGrid>
              {/* EXISTENTES (cuando vienes de edición) */}
              
            {archivosExistentes.all.length > 0 && (
  <Box mt={4} gridColumn={{ md: '1 / -1' }}>
    <FormLabel>Archivos cargados</FormLabel>

    {/* Actas de infracción */}
    {archivosExistentes.actas.length > 0 && (
      <Box mb={3}>
        <Box fontSize="sm" color="gray.600" mb={1}>Actas de infracción (PDF):</Box>
        <VStack align="start" spacing={1}>
          {archivosExistentes.actas.map((a, i) => (
            <Button
              key={a.id ?? i}
              size="sm"
              as="a"
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
            >
              Ver acta {i + 1} ({a.original_name || a.filename})
            </Button>
          ))}
        </VStack>
      </Box>
    )}

    {/* Órdenes de grúa */}
    {archivosExistentes.ordenes.length > 0 && (
      <Box mb={3}>
        <Box fontSize="sm" color="gray.600" mb={1}>Órdenes de grúa:</Box>
        <VStack align="start" spacing={1}>
          {archivosExistentes.ordenes.map((a, i) => (
            <Button
              key={a.id ?? i}
              size="sm"
              as="a"
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
            >
              Ver orden {i + 1} ({a.original_name || a.filename})
            </Button>
          ))}
        </VStack>
      </Box>
    )}

    {/* PDFs varios */}
    {archivosExistentes.pdfs.length > 0 && (
      <Box mb={3}>
        <Box fontSize="sm" color="gray.600" mb={1}>PDFs anexos:</Box>
        <VStack align="start" spacing={1}>
          {archivosExistentes.pdfs.map((a, i) => (
            <Button
              key={a.id ?? i}
              size="sm"
              as="a"
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
            >
              Ver PDF {i + 1} ({a.original_name || a.filename})
            </Button>
          ))}
        </VStack>
      </Box>
    )}

    {/* Fotos (miniaturas + ver) */}
    {archivosExistentes.fotos.length > 0 && (
      <Box mb={3}>
        <Box fontSize="sm" color="gray.600" mb={2}>Fotografías:</Box>
        <HStack wrap="wrap" spacing={3}>
          {archivosExistentes.fotos.map((a, i) => (
            <VStack key={a.id ?? i} spacing={1}>
              {/* ojo: si tu app está en https y la URL es http, el thumbnail puede bloquearse por mixed-content;
                  el botón “Ver” sí abre en pestaña nueva */}
              <Box
                as="img"
                src={a.url}
                alt={a.original_name || a.filename}
                maxW="120px"
                maxH="120px"
                objectFit="cover"
                border="1px solid #e2e8f0"
                borderRadius="md"
              />
              <Button
                size="xs"
                as="a"
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
              >
                Ver
              </Button>
            </VStack>
          ))}
        </HStack>
      </Box>
    )}
  </Box>
)}

            </Box>

            {/* Observaciones */}
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Observaciones generales</FormLabel>
              <Textarea
                name="observaciones"
                value={form.observaciones || ''}
                onChange={handleChange}
              />
            </FormControl>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter>
          <Button bg="#5b182b" color="white" _hover={{ bg: "#8a1f35" }} onClick={handleGuardar} mr={3}>
            Guardar
          </Button>
          <Button onClick={onClose}>Cancelar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
